import { z } from 'zod';
import { ARTIFACT_MAX_LEVEL, type ScaledArtifactEffects } from '../inventory/artifacts';
import { raritySchema } from '../rarity';
import { decimalStringSchema } from './primitives';

/**
 * Artifacts on the wire (prompt 3D).
 *
 * What an artifact IS and what it is currently doing. The mutation that merges
 * two copies lives in `artifact-combine.ts`, because its answer carries a hunt
 * session and the hunt's own contract already reports drops from here - the
 * chain has to stay a chain.
 *
 * Every effect crosses as a **decimal string in an exact integer unit**, never
 * a JSON number and never a float. At level 10 an artifact is worth 512x its
 * level-1 value, and 0.06 attacks/second becomes 30.72 - which is exact as
 * 30 720 000 micro-attacks and is not exact as a float that has been doubled
 * ten times.
 */

export const ARTIFACTS_ROUTE = '/api/artifacts' as const;

/**
 * One artifact's effects, in the units `inventory/artifacts.ts` defines:
 * basis points for percentages, whole Gold per kill, milli-health per second,
 * micro-attacks per second, micro percentage points for the Baby Alien chance.
 */
export const artifactEffectsSchema = z.object({
  damageBps: decimalStringSchema,
  maxHealthBps: decimalStringSchema,
  baseHealth: decimalStringSchema,
  baseAttackSpeedMicro: decimalStringSchema,
  goldPerKill: decimalStringSchema,
  goldBps: decimalStringSchema,
  regenMilliPerSecond: decimalStringSchema,
  /**
   * Modelled and displayed only. Baby Aliens are NOT released: there is no
   * definition, no sprite and no drop, and this number cannot produce one.
   */
  babyAlienChanceMicroPp: decimalStringSchema,
});
export type ArtifactEffectsWire = z.infer<typeof artifactEffectsSchema>;

/**
 * Wire effects back into exact bigints.
 *
 * The client needs them to configure its own simulation, and it must get the
 * SAME integers the server re-simulates with - so they are parsed from the
 * decimal strings rather than routed through `Number`, which stops being exact
 * long before ten doublings of a level-1 value do.
 */
export function effectsFromWire(wire: ArtifactEffectsWire): ScaledArtifactEffects {
  return {
    damageBps: BigInt(wire.damageBps),
    maxHealthBps: BigInt(wire.maxHealthBps),
    baseHealth: BigInt(wire.baseHealth),
    baseAttackSpeedMicro: BigInt(wire.baseAttackSpeedMicro),
    goldPerKill: BigInt(wire.goldPerKill),
    goldBps: BigInt(wire.goldBps),
    regenMilliPerSecond: BigInt(wire.regenMilliPerSecond),
    babyAlienChanceMicroPp: BigInt(wire.babyAlienChanceMicroPp),
  };
}

/** One owned copy of an artifact, as the inventory shows it. */
export const artifactCopySchema = z.object({
  instanceId: z.uuid(),
  level: z.number().int().min(1).max(ARTIFACT_MAX_LEVEL),
  /**
   * True for the ONE copy whose effects are applied.
   *
   * Copies of the same artifact never stack: the highest level wins, and a tie
   * is broken deterministically for display. Every other copy is inert until
   * it is merged or sold - which is why the UI labels it, rather than letting
   * a player believe three copies are three bonuses.
   */
  passiveActive: z.boolean(),
  /** True when a listing or a trade is holding it; it still grants its passive. */
  reserved: z.boolean(),
});
export type ArtifactCopyWire = z.infer<typeof artifactCopySchema>;

export const artifactStateSchema = z.object({
  definitionId: z.string().min(1),
  name: z.string().min(1),
  rarity: raritySchema,
  assetKey: z.string().min(1),
  maxLevel: z.literal(ARTIFACT_MAX_LEVEL),
  /** Every owned copy, strongest first. Empty when the player has none. */
  copies: z.array(artifactCopySchema),
  /** The level actually granting the passive; null when none is owned. */
  effectiveLevel: z.number().int().min(1).max(ARTIFACT_MAX_LEVEL).nullable(),
  /** What this artifact contributes right now. All zeroes when not owned. */
  effects: artifactEffectsSchema,
  /** What level 1 is worth, so the UI can explain an artifact nobody owns. */
  level1Effects: artifactEffectsSchema,
  /** What the NEXT level would be worth; null at level 10 or when unowned. */
  nextLevelEffects: artifactEffectsSchema.nullable(),
  /**
   * How many merges are available right now: pairs of copies at the same
   * level, below the cap. Two copies at level 3 and one at level 5 is one pair.
   */
  combinablePairs: z.number().int().min(0),
});
export type ArtifactStateWire = z.infer<typeof artifactStateSchema>;

export const artifactsResponseSchema = z.object({
  /** All ten, always, owned or not - the Overview lists the whole set. */
  artifacts: z.array(artifactStateSchema),
  /** The sum across DEFINITIONS. Copies of one artifact contribute once. */
  totals: artifactEffectsSchema,
  /** Bumped when the meaning of the artifact catalog changes. */
  contentVersion: z.number().int().min(1),
});
export type ArtifactsResponse = z.infer<typeof artifactsResponseSchema>;

/* -------------------------------------------------------------------------- */
/*  Drops                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * One artifact a settlement confirmed, as the client is allowed to see it.
 *
 * It carries no seed, no roll, no digest and no probability: the draw is
 * secret, and telling the browser anything about it would be the first step to
 * predicting the next one. Only the FACT of the drop crosses.
 */
export const artifactDropSchema = z.object({
  /** Server-derived, stable, and unique per account: the notification key. */
  dropId: z.string().min(1),
  definitionId: z.string().min(1),
  name: z.string().min(1),
  rarity: raritySchema,
  assetKey: z.string().min(1),
  instanceId: z.uuid(),
  /** Always 1. Levels come from merging, never from luck. */
  level: z.literal(1),
  /** True when a boss produced it - six times likelier than a normal kill. */
  fromBoss: z.boolean(),
});
export type ArtifactDropWire = z.infer<typeof artifactDropSchema>;
